/**
 * Modal d'assignation d'une worksheet à un employé
 */

import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Worksheet } from '../../types/worksheets.types';
import { assignWorksheet } from '../../services/worksheets.api';
import api from '../../services/api';

interface Vendor {
  id: string;
  name: string;
  email: string | null;
  role: string;
  active: boolean;
}

interface WorksheetAssignModalProps {
  worksheet: Worksheet;
  onClose: () => void;
}

export default function WorksheetAssignModal({
  worksheet,
  onClose,
}: WorksheetAssignModalProps) {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load vendors
  useEffect(() => {
    const loadVendors = async () => {
      try {
        setLoadingVendors(true);
        const tenantData = localStorage.getItem('tenant');
        if (!tenantData) return;

        const tenant = JSON.parse(tenantData);
        const vendorsList = await api.listVendors(tenant.id);
        // Filter active vendors only
        setVendors(vendorsList.filter((v: Vendor) => v.active));
      } catch (err) {
        console.error('Failed to load vendors:', err);
      } finally {
        setLoadingVendors(false);
      }
    };

    loadVendors();
  }, []);

  const handleSubmit = async () => {
    if (!selectedVendorId) {
      setError('Veuillez sélectionner un employé');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const selectedVendor = vendors.find((v) => v.id === selectedVendorId);

      await assignWorksheet(worksheet.id, {
        assigned_to_id: selectedVendorId,
        assigned_to_name: selectedVendor?.name,
      });

      onClose();
    } catch (err: any) {
      console.error('Assignment failed:', err);
      setError(err.response?.data?.message || "Erreur lors de l'assignation");
    } finally {
      setLoading(false);
    }
  };

  const selectedVendor = vendors.find((v) => v.id === selectedVendorId);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Assigner la Worksheet"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !selectedVendorId}>
            {loading ? 'En cours...' : 'Assigner'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Worksheet info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-900">{worksheet.title}</h3>
          {worksheet.description && (
            <p className="text-sm text-gray-500 mt-1">{worksheet.description}</p>
          )}
        </div>

        {/* Vendor selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Assigner à *
          </label>
          {loadingVendors ? (
            <div className="py-4 text-center text-gray-500">
              Chargement des employés...
            </div>
          ) : vendors.length === 0 ? (
            <div className="py-4 text-center text-gray-500">
              Aucun employé disponible
            </div>
          ) : (
            <Select
              value={selectedVendorId}
              onChange={(e) => setSelectedVendorId(e.target.value)}
              className="w-full"
            >
              <option value="">Sélectionner un employé...</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name} ({vendor.role})
                </option>
              ))}
            </Select>
          )}
        </div>

        {/* Selected vendor info */}
        {selectedVendor && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center">
                <span className="text-blue-700 font-medium text-lg">
                  {selectedVendor.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-medium text-blue-900">{selectedVendor.name}</p>
                <p className="text-sm text-blue-700">{selectedVendor.role}</p>
                {selectedVendor.email && (
                  <p className="text-sm text-blue-600">{selectedVendor.email}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Info message */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
          <p>
            L'employé sélectionné recevra cette worksheet dans son interface de
            travail et pourra commencer à exécuter les tâches.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
